import { classNames } from 'tradera/utils/class-names';
import { Space } from 'tradera/components/atoms/space/space';
import { Text } from 'tradera/components/atoms/text/text';
import { DropdownIcon } from 'tradera/components/dropdown-button/dropdown-button';
import type React from 'react';
import { forwardRef, useEffect, useRef } from 'react';
import AlertIcon from '@tradera/blueprint/theme/icons2/alert.svg?jsx';
import styles from './option-field.module.scss';

export type OptionFieldProps = {
    title: string;
    icon?: React.ReactNode;
    valueText?: string | null;
    wrapperClass?: string;
    placeholder: string;
    isOpen: boolean;
    disabled?: boolean;
    error?: boolean | string;
    truncateValueLeft?: boolean;
    bottomBorder?: boolean;
} & React.HTMLAttributes<HTMLElement>;

export const OptionField = forwardRef<
    React.ElementRef<'div'>,
    OptionFieldProps
>(
    (
        {
            title,
            icon,
            valueText,
            wrapperClass,
            placeholder,
            isOpen,
            disabled,
            error,
            children,
            truncateValueLeft = false,
            bottomBorder = true,
            ...props
        },
        ref
    ) => {
        const valueRef = useRef<HTMLParagraphElement>(null);

        useEffect(() => {
            const element = valueRef.current;
            // we're trying to find out if the label text should be truncated or not
            if (truncateValueLeft && element) {
                // the truncate class affect the result so we need to remove it.
                element.classList.remove(styles.truncateLeft);
                if (element.scrollWidth > element.clientWidth) {
                    element.classList.add(styles.truncateLeft);
                }
            }
        }, [truncateValueLeft, valueText]);

        return (
            <Space
                ref={ref}
                role="menu"
                tabIndex={0}
                gap={1}
                className={classNames(
                    wrapperClass,
                    { 'text-gray-400': disabled },
                    bottomBorder && 'border-bottom',
                    'pointer-events-auto w-100 cursor-pointer  py-2 overflow-hidden'
                )}
                onPointerOver={props.onPointerOver}
                {...props}>
                <Text as="label" font="hansen" nowrap className="flex-grow-1">
                    {title}
                </Text>
                {icon && icon}
                {error ? (
                    <Text
                        as="span"
                        ref={valueRef}
                        className={classNames(
                            styles.value,
                            'd-flex align-items-center'
                        )}
                        align="right"
                        fontWeight="medium"
                        color="danger">
                        <AlertIcon className="icon icon-sm mr-1" />
                        {typeof error === 'boolean'
                            ? valueText || placeholder
                            : error}
                    </Text>
                ) : valueText ? (
                    <Text
                        as="span"
                        ref={valueRef}
                        className={styles.value}
                        align="right"
                        fontWeight="medium"
                        color={disabled ? 'gray-400' : undefined}>
                        {valueText}
                    </Text>
                ) : (
                    <Text
                        as="span"
                        ref={valueRef}
                        className={styles.value}
                        align="right"
                        fontWeight="medium"
                        color={disabled ? 'gray-400' : 'gray-600'}>
                        {placeholder}
                    </Text>
                )}
                <DropdownIcon
                    isOpen={isOpen && !disabled}
                    className="flex-shrink-0"
                />
            </Space>
        );
    }
);
OptionField.displayName = 'OptionField';
